import {Injectable} from '@angular/core';
import {HttpParams, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, concatMap, filter, from, Observable, of, shareReplay, switchMap, toArray} from 'rxjs';
import {map} from 'rxjs/operators';
import {ProductChange} from '../../model/product-change';
import {EnvironmentService} from '../environment/environment.service';
import {ProductChangeDto} from './product-change-dto';
import {ProductChangeOrder} from './product-change-order';
import {HttpClientService} from '../http-client/http-client.service';
import {ProductChangeResponseDto} from './product-change-response-dto';
import {FilterContext, FilterService} from '../filter/filter.service';
import {DeliveryTypeDto} from '../delivery/delivery-type-dto';
import {DeliveryType} from '../../model/delivery-type';
import {ProductChangeResponse} from '../../model/product-change-response';
import {PersonalService} from '../personal/personal.service';
import {ProductFilterElement} from '../../model/filter';
import {TypeGuards} from '../../utils/TypeGuards';


@Injectable({
  providedIn: 'root'
})
export class ProductChangeService {

  // Größe der Product-Change-Pakete, die vom BE für die GoTo-Anzeige geladen werden
  private readonly bucketSize = 100;

  // === API URLs Configuration ===
  private readonly productChangesResource = this.environmentService.beUrl + 'productchanges';

  // === HTTP Parameter Templates ===
  private readonly parameterTemplates = {
    top: 'top',
    skip: 'skip',
    orderBy: 'orderby',
    filterProducts: 'filter-products',
    filterAssociated: 'filter-associated',
    filterProductVersion: 'filter-productversion',
    filterIris: 'filter-iri',
    separator: ','
  } as const;

  // === Resource URL Templates ===
  private readonly resourceTemplates = {
    productChangeById: (id: string) => `${this.productChangesResource}/${id}`,
  } as const;

  // === Service Configuration ===
  sortOrderSelection = ProductChangeOrder.DeliveryDateDesc;
  private latestProductChange?: [string, Observable<ProductChange>];

  constructor(private readonly httpClientService: HttpClientService,
              private readonly environmentService: EnvironmentService,
              private readonly filterService: FilterService,
              private readonly personal: PersonalService) { }

  // === HTTP Parameter Building Utilities ===

  /**
   * Builds basic pagination and ordering parameters
   */
  private buildBasicParams(top?: number, skip?: number, orderBy?: ProductChangeOrder): HttpParams {
    let params = new HttpParams();

    if (top !== undefined) {
      params = params.append(this.parameterTemplates.top, top);
    }
    if (skip !== undefined) {
      params = params.append(this.parameterTemplates.skip, skip);
    }
    if (orderBy !== undefined) {
      params = params.append(this.parameterTemplates.orderBy, `${orderBy}`);
    }

    return params;
  }

  /**
   * Adds filter parameters to existing HttpParams
   */
  private addFilterParams(params: HttpParams, filterType: keyof typeof this.parameterTemplates, filterValues: string[]): HttpParams {
    if (filterValues.length > 0) {
      const filterParam = this.parameterTemplates[filterType] as string;
      return params.append(filterParam, filterValues.join(this.parameterTemplates.separator));
    }
    return params;
  }

  /**
   * Builds product version filter parameter
   */
  private buildProductVersionFilter(productId: string, productVersion: string): string {
    return productVersion.length > 0 ? `${productId},${productVersion}` : productId;
  }

  // === Common HTTP Request Logic ===

  /**
   * Executes a product changes request with consistent error handling and caching
   */
  private executeProductChangesRequest(httpParams: HttpParams): Observable<ProductChangeResponse> {
    return this.httpClientService.get<ProductChangeResponseDto>(`${this.productChangesResource}?` + httpParams.toString())
      .pipe(
        map((resp: HttpResponse<ProductChangeResponseDto>) => ProductChangeService.mapProductChangeResponse(resp.body as ProductChangeResponseDto)),
        shareReplay(1)
      );
  }

  /**
   * Loads ALL data using pagination with configurable batch size
   * Uses shareReplay to prevent multiple executions when multiple subscribers exist
   */
  private loadAllDataWithPagination(
    batchLoader: (skip: number) => Observable<ProductChangeResponse>,
    batchSize: number
  ): Observable<ProductChangeResponse> {
    return batchLoader(0).pipe(
      switchMap(firstResponse => {
        const totalItems = firstResponse?.totalItems ?? 0;
        const firstChanges = firstResponse?.productChanges ?? [];

        if (totalItems <= batchSize) {
          return of({ productChanges: firstChanges, totalItems });
        }

        const totalBatches = Math.max(0, Math.ceil(totalItems / batchSize));
        const skipValues: number[] = [];
        for (let i = 1; i < totalBatches; i++) skipValues.push(i * batchSize);

        if (skipValues.length === 0) {
          return of({ productChanges: firstChanges, totalItems });
        }

        return from(skipValues).pipe(
          concatMap(skip => batchLoader(skip)),
          toArray(),
          map(otherResponses => {
            const allResponses = [firstResponse, ...otherResponses];
            const allChanges = allResponses.flatMap(r => r.productChanges ?? []);
            return { productChanges: allChanges, totalItems };
          })
        );
      }),
      shareReplay(1)
    );
  }


  /**
   * Executes a single product change request with consistent error handling and caching
   */
  private executeProductChangeRequest(url: string): Observable<ProductChange> {
    return this.httpClientService.get<ProductChangeDto>(url)
      .pipe(
        map((resp: HttpResponse<ProductChangeDto>) => ProductChangeService.mapProductChange(resp.body as ProductChangeDto)),
        shareReplay(1)
      );
  }

  // === Response Mapping ===

  /**
   * Maps ProductChangeResponseDto to ProductChangeResponse
   * Preserves ALL elements including NULL values as per requirements
   */
  static mapProductChangeResponse(productChangeResponseDto: ProductChangeResponseDto): ProductChangeResponse {
    // Handle null response case explicitly
    if (productChangeResponseDto === null || productChangeResponseDto === undefined) {
      return new Object(null) as ProductChangeResponse;
    }

    const productChanges: ProductChange[] = [];

    // Preserve NULL elements - do NOT filter them out
    if (productChangeResponseDto.product_changes !== null && productChangeResponseDto.product_changes !== undefined) {
      productChangeResponseDto.product_changes.forEach(productChangeDto => {
        // Map each element, including potential NULL results
        const mappedChange = ProductChangeService.mapProductChange(productChangeDto);
        productChanges.push(mappedChange);
      });
    }

    return {
      productChanges,
      totalItems: productChangeResponseDto.total_items ?? 0
    };
  }

  /**
   * Maps ProductChangeDto to ProductChange with robust null handling
   * Returns null object for invalid data but preserves in collection
   */
  static mapProductChange(productChangeDto: ProductChangeDto): ProductChange {
    // Return null object for invalid input while preserving the element
    if (productChangeDto === null ||
        productChangeDto === undefined ||
        !TypeGuards.isNonEmptyString(productChangeDto.id) ||
        !TypeGuards.isNonEmptyString(productChangeDto.title) ||
        !TypeGuards.isNonEmptyString(productChangeDto.delivery_date) ||
        !TypeGuards.isDate(new Date(productChangeDto.delivery_date))) {
      return new Object(null) as ProductChange;
    }

    const deliveryType = ProductChangeService.mapDeliveryType(productChangeDto.delivery_type);

    return {
      id: productChangeDto.id,
      title: productChangeDto.title,
      teaser: productChangeDto.teaser ?? [],
      documentId: productChangeDto.document_id ?? '',
      changeCategories: productChangeDto.change_categories ?? [],
      deliveryDate: new Date(productChangeDto.delivery_date),
      dataTransformation: productChangeDto.data_transformation ?? false,
      bootRequired: productChangeDto.boot_required ?? false,
      deliveryBases: productChangeDto.delivery_bases ?? [],
      deliveryId: productChangeDto.delivery_id ?? '',
      deliveryName: productChangeDto.delivery_name ?? '',
      deliveryType,
      productKey: productChangeDto.product_key ?? '',
      deliveredByRdt: productChangeDto.delivered_by_rdt ?? true,
      deliveredByDownload: productChangeDto.delivered_by_download ?? false,
      onlineProduct: productChangeDto.online_product ?? false
    };
  }

  /**
   * Maps DeliveryTypeDto to DeliveryType with null safety
   */
  static mapDeliveryType(deliveryTypeDto: DeliveryTypeDto | null | undefined): DeliveryType | null {
    switch (deliveryTypeDto) {
      case DeliveryTypeDto.MainRelease:
        return DeliveryType.MainRelease;
      case DeliveryTypeDto.ServiceRelease:
        return DeliveryType.ServiceRelease;
      case DeliveryTypeDto.Hotfix:
        return DeliveryType.Hotfix;
      default:
        return null;
    }
  }

  // === Public API Methods ===

  /**
   * Retrieves product changes with pagination, filtering, and sorting
   *
   * @param top - Maximum number of items to return
   * @param skip - Number of items to skip for pagination
   * @param orderBy - Optional sorting order (defaults to service configuration)
   * @param businessPartnerFilterIsActive - Whether to apply business partner filtering
   * @returns Observable of ProductChangeResponse containing filtered and paginated results
   *
   * @example
   * ```typescript
   * service.getProductChanges(20, 0, ProductChangeOrder.DeliveryDateDesc, false)
   *   .subscribe(response => console.log(response.productChanges));
   * ```
   */
  getProductChanges(top: number, skip: number, orderBy?: ProductChangeOrder,
                    businessPartnerFilterIsActive: boolean = false): Observable<ProductChangeResponse> {

    return this.filterService.getSelectedProductFilter(FilterContext.ProductChange).pipe(
        // der erste Filterwert ist ausreichend, außer es ist ein leerer Filter
        filter((selectedFilter: ProductFilterElement[], index: number) => {
          return index === 0 || selectedFilter.length === 0;
        }
      ),
      switchMap(selectedFilter => {
        let httpParams = this.buildBasicParams(top, skip, orderBy);

        httpParams = this.addFilterParams(httpParams, 'filterProducts',
            selectedFilter.map(item => item.id));

        if (businessPartnerFilterIsActive) {
          return this.personal.activeBusinessPartner$.pipe(
            switchMap(businessPartner => this.personal.getBusinessPartnerFilterHKNRs(businessPartner.businessPartnerId)),
            switchMap(filterHKNRs => {
              httpParams = httpParams.append(this.parameterTemplates.filterAssociated,
                filterHKNRs.join(this.parameterTemplates.separator));
              return this.httpClientService.get<ProductChangeResponseDto>(`${this.productChangesResource}?` + httpParams.toString())
                .pipe(
                  map((resp: HttpResponse<ProductChangeResponseDto>) => ProductChangeService.mapProductChangeResponse(resp.body as ProductChangeResponseDto))
                );
            })
          );
        } else {
          return this.executeProductChangesRequest(httpParams);
        }
      })
    );
  }

  /**
   * Retrieves a single product change by its unique identifier
   *
   * @param id - Unique identifier of the product change
   * @returns Observable of ProductChange or null object if not found
   *
   * @example
   * ```typescript
   * service.getProductChangeById('change-123')
   *   .subscribe(change => console.log(change.title));
   * ```
   */
  getProductChangeById(id: string): Observable<ProductChange> {
    if (!this.latestProductChange || this.latestProductChange[0] !== id) {
      const productChangeObservable = this.executeProductChangeRequest(
        this.resourceTemplates.productChangeById(id)
      );
      this.latestProductChange = [id, productChangeObservable];
    }
    return this.latestProductChange[1];
  }

  /**
   * Retrieves product changes filtered by product version
   * Loads ALL data using pagination
   *
   * @param filterProductId - Product identifier to filter by
   * @param filterProductVersion - Product version to filter by (empty string for all versions)
   * @param orderBy - Optional sorting order
   * @returns Observable of ProductChangeResponse containing ALL filtered results
   *
   * @example
   * ```typescript
   * service.getProductChangesByProductVersion('product-123', '2.1.0', ProductChangeOrder.DeliveryDateAsc)
   *   .subscribe(response => console.log(response.productChanges));
   * ```
   */
  getProductChangesByProductVersion(filterProductId: string, filterProductVersion: string,
                                    orderBy?: ProductChangeOrder): Observable<ProductChangeResponse> {
    const filterProduct = this.buildProductVersionFilter(filterProductId, filterProductVersion);

    const loadBatch = (skip: number): Observable<ProductChangeResponse> => {
      let httpParams = this.buildBasicParams(this.bucketSize, skip, orderBy);
      httpParams = httpParams.append(this.parameterTemplates.filterProductVersion, filterProduct);
      return this.executeProductChangesRequest(httpParams);
    };

    return this.loadAllDataWithPagination(loadBatch, this.bucketSize);
  }

  /**
   * Retrieves product changes filtered by IRIS identifiers
   * Loads ALL data using pagination with 100-item batches
   *
   * @param filterIris - Array of IRIS identifiers to filter by
   * @param orderBy - Optional sorting order
   * @returns Observable of ProductChangeResponse containing ALL filtered results
   *
   * @example
   * ```typescript
   * service.getProductChangesByIris(['IRIS123', 'IRIS456'], ProductChangeOrder.DeliveryDateDesc)
   *   .subscribe(response => console.log(response.productChanges));
   * ```
   */
  getProductChangesByIris(filterIris: string[], orderBy?: ProductChangeOrder): Observable<ProductChangeResponse> {

    const loadBatch = (skip: number): Observable<ProductChangeResponse> => {
      let httpParams = this.buildBasicParams(this.bucketSize, skip, orderBy);
      httpParams = this.addFilterParams(httpParams, 'filterIris', filterIris);
      return this.executeProductChangesRequest(httpParams);
    };

    return this.loadAllDataWithPagination(loadBatch, this.bucketSize);
  }

  /**
   * Sets the latest product change for caching purposes
   *
   * @param productChange - ProductChange instance to cache
   *
   * @example
   * ```typescript
   * service.setLatestProductChange(selectedProductChange);
   * ```
   */
  setLatestProductChange(productChange: ProductChange): void {
    const productChange$ = new BehaviorSubject<ProductChange>(productChange);
    this.latestProductChange = [productChange.id, productChange$];
  }

}
